import React from "react";
import { Modal } from "antd";
import {
  SKILL_LEVEL_REFERENCE_ROWS,
  SkillLevelNamingState,
  SkillLevelReferenceRow,
} from "@/constants/skillLevelStateNaming";

type Props = {
  open: boolean;
  onClose: () => void;
  rows?: SkillLevelReferenceRow[];
  highlightState?: SkillLevelNamingState;
};

export default function SkillLevelNamingTableModal({
  open,
  onClose,
  rows = SKILL_LEVEL_REFERENCE_ROWS,
  highlightState = "DEFAULT",
}: Props) {
  const sortedRows = [...rows].sort((a, b) => a.rank - b.rank);

  const highlightClass = (state: SkillLevelNamingState) =>
    highlightState === state
      ? "bg-indigo-50 font-semibold text-indigo-900"
      : "text-gray-700";

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={1100}
      title="Australian skill level naming by state"
      destroyOnClose
    >
      <p className="text-sm text-gray-600 mb-4">
        StatDoctor uses one skill ladder (sk-1 … sk-6). Each state uses different
        job titles for the same tier. Shift minimums mean that level{" "}
        <strong>and higher</strong> may apply.
      </p>
      <div className="overflow-x-auto rounded-xl border border-gray-200">
        <table className="min-w-full text-left text-xs sm:text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="px-3 py-2 font-semibold">NSW</th>
              <th className="px-3 py-2 font-semibold">VIC</th>
              <th className="px-3 py-2 font-semibold">QLD</th>
              <th className="px-3 py-2 font-semibold">ACT / Other</th>
              <th className="px-3 py-2 font-semibold min-w-[220px]">
                Responsibility / PGY
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row) => (
              <tr key={row.skillId} className="border-t border-gray-100 align-top">
                <td className={`px-3 py-3 ${highlightClass("NSW")}`}>{row.nsw}</td>
                <td className={`px-3 py-3 ${highlightClass("VIC")}`}>{row.vic}</td>
                <td className={`px-3 py-3 ${highlightClass("QLD")}`}>{row.qld}</td>
                <td className={`px-3 py-3 ${highlightClass("ACT")}`}>{row.act}</td>
                <td className="px-3 py-3 text-gray-600">{row.responsibility}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {highlightState !== "DEFAULT" && (
        <p className="mt-3 text-xs text-indigo-700">
          Your portal is using <strong>{highlightState}</strong> naming (highlighted
          column). Switch back to Default in Account → Skill level naming.
        </p>
      )}
    </Modal>
  );
}
